import {
  DICA_ESTILO,
  DICA_PASSE,
  DICA_PRESSAO,
  FORMACOES,
  LISTA_ESTILOS,
  LISTA_FORMACOES,
  LISTA_PASSES,
  LISTA_PRESSOES,
  MAX_PAPEIS,
  PAPEIS,
  ROTULO_ESTILO,
  ROTULO_PASSE,
  ROTULO_PRESSAO,
  aptidaoPapel,
  condicaoDaEscalacao,
  leituraTatica,
  papeisDaPosicao,
  clubePorId,
  disponivel,
  escalarAutoFormacao,
  fatorPosicao,
  forcaBase,
  MAX_AJUSTES,
  MAX_PLANO_SUBS,
  normalizarPlano,
  resolverEscalacao,
  slotsDaFormacao,
  type Clube,
  type CondicaoJogo,
  type ConcentracaoAtaque,
  type Estilo,
  type Formacao,
  type Jogador,
  type Marcacao,
  type Papel,
  type Passe,
  type Pressao,
  type PlanoDeJogo,
  type Posicao,
  BANCO_MAX,
} from '../core';
import { app, temporadaAcabou, humano, jogoAtual, type EstadoEscalar } from './app';
import { camisa, camisaVazia, escudo, rosto } from './icones';
import { dialogo } from './dialogos';
import { NOME_POSICAO, esc } from './util';

// BANCO_MAX (12) vem do núcleo

export function iniciarEscalar(): void {
  const c = humano();
  const r = resolverEscalacao(c);
  const t = c.tatica;
  // a opção antiga "Falso 9" virou a função individual de mesmo nome: quem tinha marcado passa a ter o primeiro atacante escalado nela
  const papeis: Record<number, Papel> = { ...(t.papeis ?? {}) };
  if (t.falso9 && !Object.values(papeis).includes('falso9') && Object.keys(papeis).length < MAX_PAPEIS) {
    const slotsR = slotsDaFormacao(r.formacao);
    const atacante = r.escalacao.titulares.find((_, i) => slotsR[i] === 'A');
    if (atacante) papeis[atacante.id] = 'falso9';
  }
  app.escalar = {
    formacao: t.formacao === 'auto' && !t.titulares ? 'auto' : r.formacao,
    titulares: r.escalacao.titulares.map((j) => j.id),
    reservas: r.escalacao.reservas.map((j) => j.id),
    estilo: t.estilo,
    marcacao: t.marcacao,
    ataque: t.ataque,
    passe: t.passe ?? 'misto',
    pressao: t.pressao ?? 'media',
    selSlot: null,
    selJogador: null,
    manter: true,
    painel: 'time',
    plano: (t.substituicoes ?? []).map((s) => ({ ...s })),
    ajustes: (t.ajustes ?? []).map((a) => ({ ...a })),
    funcoes: {
      capitaoId: t.capitaoId ?? null,
      batedorPenaltiId: t.batedorPenaltiId ?? null,
      batedorFaltaId: t.batedorFaltaId ?? null,
      batedorEscanteioId: t.batedorEscanteioId ?? null,
      falso9: false,
      papeis,
    },
  };
}

// ---- Plano de jogo: substituições planejadas, ajustes do intervalo e funções -------------------

const CONDICOES: [CondicaoJogo, string][] = [
  ['sempre', 'Sempre'],
  ['perdendo', 'Se estiver perdendo'],
  ['empatando', 'Se estiver empatando'],
  ['ganhando', 'Se estiver ganhando'],
];
const MINUTOS = [0, 15, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85];

function opcoes<T extends string | number>(atual: T | null | undefined, itens: [T, string][], vazio?: string): string {
  const v = vazio !== undefined ? `<option value="" ${atual === null || atual === undefined ? 'selected' : ''}>${vazio}</option>` : '';
  return v + itens.map(([valor, nome]) => `<option value="${valor}" ${atual === valor ? 'selected' : ''}>${esc(nome)}</option>`).join('');
}

function painelPlano(c: Clube, st: EstadoEscalar): string {
  const titulares = st.titulares.map((id) => jogadorPorId(c, id)).filter((j): j is Jogador => Boolean(j));
  const reservas = st.reservas.map((id) => jogadorPorId(c, id)).filter((j): j is Jogador => Boolean(j));
  const rotulo = (j: Jogador): string => `${j.posicao} · ${j.nome} (F:${j.overall} E:${j.energia})`;
  const jogadoresCampo = titulares.map((j): [number, string] => [j.id, rotulo(j)]);
  const jogadoresReserva = reservas.map((j): [number, string] => [j.id, rotulo(j)]);
  const todos = [...titulares, ...reservas].map((j): [number, string] => [j.id, `${j.posicao} · ${j.nome}`]);

  const subs = st.plano
    .map(
      (s, i) => `<div class="pl-linha">
        <label>Sai<select data-ch="esc-pl-sai-${i}">${opcoes(s.saiId, jogadoresCampo, '— escolha —')}</select></label>
        <label>Entra<select data-ch="esc-pl-entra-${i}">${opcoes(s.entraId, jogadoresReserva, '— escolha —')}</select></label>
        <label>Quando<select data-ch="esc-pl-min-${i}">${opcoes(s.minuto, MINUTOS.map((m): [number, string] => [m, m === 0 ? 'Intervalo' : `${m} min`]))}</select></label>
        <label>Se<select data-ch="esc-pl-cond-${i}">${opcoes(s.condicao, CONDICOES)}</select></label>
        <button type="button" class="btn peq" data-a="esc-pl-del" data-i="${i}" aria-label="Remover substituição">✕</button></div>`,
    )
    .join('');

  const ajustes = st.ajustes
    .map(
      (a, i) => `<div class="pl-linha">
        <label>No intervalo, se<select data-ch="esc-aj-cond-${i}">${opcoes(a.condicao, CONDICOES)}</select></label>
        <label>Mentalidade<select data-ch="esc-aj-estilo-${i}">${opcoes(a.estilo ?? null, LISTA_ESTILOS.map((x): [Estilo, string] => [x, ROTULO_ESTILO[x]]), 'manter')}</select></label>
        <label>Passe<select data-ch="esc-aj-passe-${i}">${opcoes(a.passe ?? null, LISTA_PASSES.map((x): [Passe, string] => [x, ROTULO_PASSE[x]]), 'manter')}</select></label>
        <label>Pressão<select data-ch="esc-aj-press-${i}">${opcoes(a.pressao ?? null, LISTA_PRESSOES.map((x): [Pressao, string] => [x, ROTULO_PRESSAO[x]]), 'manter')}</select></label>
        <label>Marcação<select data-ch="esc-aj-marc-${i}">${opcoes(a.marcacao ?? null, [['leve', 'Cautelosa'], ['normal', 'Normal'], ['forte', 'Agressiva']], 'manter')}</select></label>
        <label>Ataques<select data-ch="esc-aj-ata-${i}">${opcoes(a.ataque ?? null, [['meio', 'Pelo meio'], ['lados', 'Pelos lados'], ['equilibrado', 'Equilibrado']], 'manter')}</select></label>
        <button type="button" class="btn peq" data-a="esc-aj-del" data-i="${i}" aria-label="Remover ajuste">✕</button></div>`,
    )
    .join('');

  const f = st.funcoes;
  const funcao = (campo: string, nome: string, valor: number | null | undefined): string =>
    `<label>${nome}<select data-ch="esc-fn-${campo}">${opcoes(valor ?? null, todos, 'automático')}</select></label>`;
  return `<div class="plano">
    <div class="pn-tit verde"><span>Substituições planejadas (${st.plano.length}/${MAX_PLANO_SUBS})</span></div>
    <p class="cinza peq">Rodam sozinhas durante a partida, também no modo online. Quem sai deve estar entre os titulares e quem entra, no banco.</p>
    ${subs || '<p class="cinza">Nenhuma substituição planejada.</p>'}
    <button type="button" class="btn" data-a="esc-pl-add" ${st.plano.length >= MAX_PLANO_SUBS ? 'disabled' : ''}>+ Substituição</button>
    <div class="pn-tit"><span>Mudar a tática no intervalo (${st.ajustes.length}/${MAX_AJUSTES})</span></div>
    <p class="cinza peq">Vale o primeiro ajuste cuja condição de placar for verdadeira.</p>
    ${ajustes || '<p class="cinza">Nenhum ajuste.</p>'}
    <button type="button" class="btn" data-a="esc-aj-add" ${st.ajustes.length >= MAX_AJUSTES ? 'disabled' : ''}>+ Ajuste de intervalo</button>
    ${painelPapeis(c, st)}
    <div class="pn-tit"><span>Funções especiais</span></div>
    <div class="pl-funcoes">
      ${funcao('capitao', 'Capitão', f.capitaoId)}
      ${funcao('penalti', 'Batedor de pênaltis', f.batedorPenaltiId)}
      ${funcao('falta', 'Batedor de faltas', f.batedorFaltaId)}
      ${funcao('escanteio', 'Batedor de escanteios', f.batedorEscanteioId)}

    </div>
    <p class="cinza peq">Se o batedor escolhido não estiver em campo (lesão ou expulsão), outro cobra. O capitão só aparece com a braçadeira e não muda o desempenho.</p>
  </div>`;
}

function insignias(st: EstadoEscalar, id: number): string {
  const f = st.funcoes;
  const letras = [f.capitaoId === id ? 'C' : '', f.batedorPenaltiId === id ? 'P' : '', f.batedorFaltaId === id ? 'F' : '', f.batedorEscanteioId === id ? 'E' : ''].filter(Boolean);
  return letras.length ? `<span class="insignias" title="C capitão · P pênaltis · F faltas · E escanteios">${letras.join('')}</span>` : '';
}

function formacaoEfetiva(st: EstadoEscalar): Formacao {
  if (st.formacao !== 'auto') return st.formacao;
  return resolverEscalacao(humano()).formacao;
}

/** Posições (em %) dos slots no campo, que tem o gol embaixo e o ataque em cima. */
export function posicoesNoCampo(f: Formacao): { x: number; y: number }[] {
  const c = FORMACOES[f];
  const pos: { x: number; y: number }[] = [{ x: 50, y: 90 }]; // goleiro
  const espalhar = (n: number, de: number, ate: number): number[] => (n === 1 ? [(de + ate) / 2] : Array.from({ length: n }, (_, i) => de + ((ate - de) * i) / (n - 1)));
  // slots vêm na ordem G, Z, L, M, A
  const zs = espalhar(c.Z, c.L > 0 ? 32 : 24, c.L > 0 ? 68 : 76);
  zs.forEach((x) => pos.push({ x, y: 74 }));
  const ls = c.L === 2 ? [10, 90] : espalhar(c.L, 12, 88);
  ls.forEach((x) => pos.push({ x, y: c.Z + c.L >= 5 ? 66 : 74 }));
  espalhar(c.M, c.M >= 5 ? 12 : 16, c.M >= 5 ? 88 : 84).forEach((x) => pos.push({ x, y: 46 }));
  espalhar(c.A, c.A === 1 ? 50 : 28, c.A === 1 ? 50 : 72).forEach((x) => pos.push({ x, y: 14 }));
  return pos;
}

function jogadorPorId(c: Clube, id: number | null): Jogador | undefined {
  return id === null ? undefined : c.elenco.find((j) => j.id === id);
}

export function shirt(c: Clube, j: Jogador, slot: Posicao | null): string {
  const goleiro = slot === 'G' || (slot === null && j.posicao === 'G');
  const veste = goleiro ? camisa('#1c1c1c', '#444', false, j.posicao) : camisa(c.cor1, c.cor2, true, j.posicao);
  return `<span class="camisa-rosto">${veste}${rosto(j.nome, j.posicao, c.sigla)}</span>`;
}

export function rotuloJogador(j: Jogador, slot: Posicao | null): string {
  const fora = slot && slot !== j.posicao;
  const prefixo = slot ? '' : `${j.posicao} · `;
  return `<span class="jn" title="${esc(j.nome)} (${j.posicao}) F:${j.overall} E:${j.energia}${fora ? ' · fora de posição' : ''}">${esc(j.nome)}</span><span class="jf">${prefixo}F:${j.overall} E:${j.energia}${fora ? ' ⚠' : ''}</span>`;
}

export function dlgEscalar(): string {
  const e = jogoAtual();
  const c = humano();
  const st = app.escalar;
  if (!st) return '';
  const f = formacaoEfetiva(st);
  const slots = slotsDaFormacao(f);
  const coords = posicoesNoCampo(f);
  const emCampo = new Set(st.titulares.filter((x): x is number => x !== null));
  const noBanco = new Set(st.reservas);

  const jogadoresCampo = slots
    .map((slot, i) => {
      const j = jogadorPorId(c, st.titulares[i] ?? null);
      const p = coords[i] ?? { x: 50, y: 50 };
      const sel = st.selSlot === i ? ' sel' : '';
      const corpo = j
        ? `${shirt(c, j, slot)}${insignias(st, j.id)}${rotuloJogador(j, slot)}`
        : `${camisaVazia()}<span class="jf">${slot}</span>`;
      return `<button class="slot-campo${sel}${j ? '' : ' vazio'}" style="left:${p.x}%;top:${p.y}%" data-a="esc-slot" data-i="${i}" aria-label="${NOME_POSICAO[slot]} ${i + 1}${j ? ': ' + esc(j.nome) : ' (vazio)'}">${corpo}</button>`;
    })
    .join('');

  const banco = Array.from({ length: BANCO_MAX }, (_, i) => {
    const j = jogadorPorId(c, st.reservas[i] ?? null);
    return `<button class="slot-banco${j ? '' : ' vazio'}${st.selSlot === 100 + i ? ' sel' : ''}" data-a="esc-banco" data-i="${i}">${j ? `${shirt(c, j, null)}${rotuloJogador(j, null)}` : camisaVazia()}</button>`;
  }).join('');

  const disponiveis = c.elenco
    .filter((j) => disponivel(j) && !emCampo.has(j.id) && !noBanco.has(j.id))
    .sort((a, b) => ORDEM(a.posicao) - ORDEM(b.posicao) || b.overall - a.overall)
    .map((j) => `<button class="slot-disp${st.selJogador === j.id ? ' sel' : ''}" data-a="esc-disp" data-id="${j.id}">${shirt(c, j, null)}${rotuloJogador(j, null)}</button>`)
    .join('');
  const indisp = c.elenco.filter((j) => !disponivel(j));

  const rodada = e.calendario[e.rodada];
  const conf = rodada?.find((x) => x.mandante === e.humanoId || x.visitante === e.humanoId);
  const casa = conf?.mandante === e.humanoId;
  const adv = conf ? clubePorId(e, casa ? conf.visitante : conf.mandante) : null;

  const opcForm =
    `<option value="">Selecione opção</option><option value="reset">Resetar tudo</option><option value="auto" ${st.formacao === 'auto' ? 'selected' : ''}>Automática (melhor)</option>` +
    LISTA_FORMACOES.map((x) => `<option value="${x}" ${st.formacao === x ? 'selected' : ''}>${x}</option>`).join('');
  const opc = <T extends string>(valor: T, rotulos: [T, string][]): string => rotulos.map(([v, n]) => `<option value="${v}" ${valor === v ? 'selected' : ''}>${n}</option>`).join('');

  return dialogo(
    'Escalar time',
    `<div class="esc-grade">
      <div class="esc-esq"><div class="campo-fut">${jogadoresCampo}</div>
        <label class="check padrao"><input type="checkbox" data-ch="esc-manter" ${st.manter ? 'checked' : ''} /> escalação padrão</label>
        ${painelSalvas(c)}
        <div class="esc-adv">${adv ? `<b>Adversário:</b> ${escudo(adv)}${esc(adv.nome)} <span class="cinza">(${casa ? 'em casa' : 'fora'})</span>` : ''}</div>
        ${adv ? leituraDoAdversario(c, adv, st) : ''}</div>
      <div class="esc-dir">
        <div class="abas-esc"><button type="button" class="btn${st.painel === 'time' ? ' principal' : ''}" data-a="esc-painel" data-p="time">Escalação</button>
          <button type="button" class="btn${st.painel === 'plano' ? ' principal' : ''}" data-a="esc-painel" data-p="plano">Plano de jogo${st.plano.length + st.ajustes.length ? ` (${st.plano.length + st.ajustes.length})` : ''}</button></div>
        ${
          st.painel === 'plano'
            ? painelPlano(c, st)
            : `<div class="pn-tit verde"><span>Banco de reservas</span></div><div class="banco">${banco}</div>
        <div class="pn-tit"><span>Jogadores disponíveis</span></div><div class="disponiveis">${disponiveis || '<span class="cinza">Todos os jogadores já estão escalados.</span>'}</div>
        ${indisp.length ? `<p class="cinza peq">Indisponíveis: ${indisp.map((j) => `${esc(j.nome)} (${j.lesaoRodadas > 0 ? 'lesão ' + j.lesaoRodadas : 'susp. ' + j.suspensaoRodadas})`).join(', ')}</p>` : ''}`
        }
        <div class="controles-esc">
          <label>Seleção Automática:<select data-ch="esc-formacao">${opcForm}</select></label>
          <label>Mentalidade:<select data-ch="esc-estilo">${opc(st.estilo, LISTA_ESTILOS.map((x): [Estilo, string] => [x, ROTULO_ESTILO[x]]))}</select></label>
          <label>Passe:<select data-ch="esc-passe">${opc(st.passe, LISTA_PASSES.map((x): [Passe, string] => [x, ROTULO_PASSE[x]]))}</select></label>
          <label>Pressão:<select data-ch="esc-pressao">${opc(st.pressao, LISTA_PRESSOES.map((x): [Pressao, string] => [x, ROTULO_PRESSAO[x]]))}</select></label>
          <label>Agressividade da marcação:<select data-ch="esc-marcacao">${opc(st.marcacao, [['leve', 'Cautelosa'], ['normal', 'Normal'], ['forte', 'Agressiva (carrinhos)']])}</select></label>
          <label>Concentrar ataques:<select data-ch="esc-ataque">${opc(st.ataque, [['meio', 'Pelo meio'], ['lados', 'Pelos lados'], ['equilibrado', 'Equilibrado']])}</select></label>
          <p class="cinza peq dica-tatica">${esc(DICA_ESTILO[st.estilo])} ${esc(DICA_PASSE[st.passe])} ${esc(DICA_PRESSAO[st.pressao])}</p>
        </div>
        <div class="dlg-botoes"><button class="btn" data-a="esc-esvaziar" ${st.selSlot !== null ? '' : 'disabled'}>Esvaziar posição</button>
          <button class="btn" data-a="esc-voltar">« Voltar ao time</button>
          <button class="btn principal" data-a="esc-jogar" ${temporadaAcabou(e) ? 'disabled' : ''}>Jogar »</button></div>
      </div></div>`,
    'escalar',
    'esc-voltar',
  );
}

const ORDEM = (p: Posicao): number => ({ G: 0, L: 1, Z: 2, M: 3, A: 4 })[p];

// ---- Interação ---------------------------------------------------------------------------------

function tirarDeTodoLugar(st: EstadoEscalar, id: number): void {
  st.titulares = st.titulares.map((x) => (x === id ? null : x));
  st.reservas = st.reservas.filter((x) => x !== id);
}

/** Coloca o jogador `id` no slot do campo `i` (o ocupante anterior volta para a lista de disponíveis). */
function colocarNoSlot(st: EstadoEscalar, i: number, id: number): void {
  const c = humano();
  const j = jogadorPorId(c, id);
  const slot = slotsDaFormacao(formacaoEfetiva(st))[i];
  if (!j || !slot) return;
  if (slot === 'G' && j.posicao !== 'G') {
    app.mensagem = 'Só goleiros podem jogar no gol.';
    return;
  }
  if (slot !== 'G' && j.posicao === 'G') {
    app.mensagem = 'Goleiro não joga na linha.';
    return;
  }
  tirarDeTodoLugar(st, id);
  st.titulares[i] = id;
  st.formacao = formacaoEfetiva(st);
}

function colocarNoBanco(st: EstadoEscalar, i: number, id: number): void {
  tirarDeTodoLugar(st, id);
  const lista = [...st.reservas];
  lista.splice(Math.min(i, lista.length), 0, id);
  st.reservas = lista.slice(0, BANCO_MAX);
}

export function escClique(acao: string, d: DOMStringMap): void {
  const st = app.escalar;
  if (!st) return;
  app.mensagem = '';
  const i = Number(d.i);
  const id = Number(d.id);
  if (acao === 'esc-painel') {
    st.painel = d.p === 'plano' ? 'plano' : 'time';
    return;
  }
  if (acao === 'esc-salvar' && i >= 0 && i < SLOTS_SALVOS) {
    salvarEscalacao(st, i);
    return;
  }
  if (acao === 'esc-carregar' && i >= 0 && i < SLOTS_SALVOS) {
    carregarEscalacao(st, i);
    return;
  }
  if (acao === 'esc-pl-add') {
    if (st.plano.length < MAX_PLANO_SUBS) {
      // sugere o titular mais cansado e o primeiro reserva da mesma posição (ou o primeiro do banco)
      const cl = humano();
      const emCampo = st.titulares.map((x) => jogadorPorId(cl, x)).filter((j): j is Jogador => Boolean(j) && !st.plano.some((p) => p.saiId === (j as Jogador).id));
      const sai = [...emCampo].filter((j) => j.posicao !== 'G').sort((a, b) => a.energia - b.energia)[0];
      const entra = st.reservas.map((x) => jogadorPorId(cl, x)).find((j) => j && j.posicao === (sai?.posicao ?? 'M') && !st.plano.some((p) => p.entraId === j.id)) ?? st.reservas.map((x) => jogadorPorId(cl, x)).find((j) => j && j.posicao !== 'G' && !st.plano.some((p) => p.entraId === j.id));
      st.plano.push({ saiId: sai?.id ?? 0, entraId: entra?.id ?? 0, minuto: 60, condicao: 'sempre' });
    }
    return;
  }
  if (acao === 'esc-pl-del') {
    st.plano.splice(i, 1);
    return;
  }
  if (acao === 'esc-aj-add') {
    if (st.ajustes.length < MAX_AJUSTES) st.ajustes.push({ condicao: 'perdendo', estilo: 'ofensivo' });
    return;
  }
  if (acao === 'esc-aj-del') {
    st.ajustes.splice(i, 1);
    return;
  }
  if (acao === 'esc-slot') {
    if (st.selJogador !== null) {
      colocarNoSlot(st, i, st.selJogador);
      st.selJogador = null;
    } else if (st.selSlot === null) {
      st.selSlot = i;
    } else if (st.selSlot === i) {
      st.selSlot = null;
    } else if (st.selSlot >= 100) {
      // troca entre o banco e o campo
      const bi = st.selSlot - 100;
      const doBanco = st.reservas[bi] ?? null;
      const doCampo = st.titulares[i] ?? null;
      st.selSlot = null;
      if (doBanco !== null) colocarNoSlot(st, i, doBanco);
      if (doCampo !== null) colocarNoBanco(st, bi, doCampo);
    } else {
      const a = st.titulares[st.selSlot] ?? null;
      const b = st.titulares[i] ?? null;
      const slots = slotsDaFormacao(formacaoEfetiva(st));
      const c = humano();
      const ok = (jid: number | null, slot: Posicao | undefined): boolean => {
        const j = jorn(c, jid);
        return !j || !slot || (slot === 'G') === (j.posicao === 'G');
      };
      if (ok(a, slots[i]) && ok(b, slots[st.selSlot])) {
        st.titulares[st.selSlot] = b;
        st.titulares[i] = a;
        st.formacao = formacaoEfetiva(st);
      } else app.mensagem = 'Só goleiros jogam no gol.';
      st.selSlot = null;
    }
  } else if (acao === 'esc-banco') {
    if (st.selJogador !== null) {
      colocarNoBanco(st, i, st.selJogador);
      st.selJogador = null;
    } else if (st.selSlot !== null && st.selSlot < 100) {
      const doCampo = st.titulares[st.selSlot] ?? null;
      const doBanco = st.reservas[i] ?? null;
      const slot = st.selSlot;
      st.selSlot = null;
      if (doBanco !== null) colocarNoSlot(st, slot, doBanco);
      else st.titulares[slot] = null;
      if (doCampo !== null) colocarNoBanco(st, i, doCampo);
    } else if (st.reservas[i] !== undefined) {
      st.selSlot = st.selSlot === 100 + i ? null : 100 + i;
    }
  } else if (acao === 'esc-disp') {
    if (st.selSlot !== null && st.selSlot < 100) {
      colocarNoSlot(st, st.selSlot, id);
      st.selSlot = null;
    } else if (st.selSlot !== null && st.selSlot >= 100) {
      colocarNoBanco(st, st.selSlot - 100, id);
      st.selSlot = null;
    } else {
      st.selJogador = st.selJogador === id ? null : id;
    }
  } else if (acao === 'esc-esvaziar') {
    if (st.selSlot !== null && st.selSlot < 100) {
      st.titulares[st.selSlot] = null;
      st.formacao = formacaoEfetiva(st);
    } else if (st.selSlot !== null) {
      st.reservas.splice(st.selSlot - 100, 1);
    }
    st.selSlot = null;
  }
}

/** De onde o jogador sai ao ser arrastado. */
export type OrigemArraste = { tipo: 'slot'; i: number } | { tipo: 'banco'; i: number } | { tipo: 'disp'; id: number };
/** Onde ele é solto: posição do campo, vaga do banco ou a lista de disponíveis (tira da escalação). */
export type AlvoArraste = { tipo: 'slot'; i: number } | { tipo: 'banco'; i: number } | { tipo: 'disp' };

const dados = (o: Record<string, number>): DOMStringMap => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, String(v)])) as DOMStringMap;

/**
 * Arrastar e soltar: usa as mesmas regras do "clicar e clicar" (goleiro só no gol, troca entre campo e banco, etc.),
 * simulando a seleção da origem e o clique no alvo.
 */
export function soltarEscalacao(o: OrigemArraste, a: AlvoArraste): void {
  const st = app.escalar;
  if (!st) return;
  st.selSlot = null;
  st.selJogador = null;
  if (o.tipo === 'disp') {
    if (a.tipo === 'slot') {
      escClique('esc-disp', dados({ id: o.id }));
      escClique('esc-slot', dados({ i: a.i }));
    } else if (a.tipo === 'banco') {
      escClique('esc-disp', dados({ id: o.id }));
      escClique('esc-banco', dados({ i: a.i }));
    }
  } else if (o.tipo === 'slot') {
    if (a.tipo === 'slot' && a.i === o.i) return;
    escClique('esc-slot', dados({ i: o.i }));
    if (a.tipo === 'slot') escClique('esc-slot', dados({ i: a.i }));
    else if (a.tipo === 'banco') escClique('esc-banco', dados({ i: a.i }));
    else escClique('esc-esvaziar', dados({}));
  } else {
    const id = st.reservas[o.i];
    if (id === undefined) return;
    if (a.tipo === 'slot') {
      escClique('esc-banco', dados({ i: o.i }));
      escClique('esc-slot', dados({ i: a.i }));
    } else if (a.tipo === 'banco') {
      if (a.i !== o.i) {
        tirarDeTodoLugar(st, id);
        colocarNoBanco(st, a.i, id);
      }
    } else {
      escClique('esc-banco', dados({ i: o.i }));
      escClique('esc-esvaziar', dados({}));
    }
  }
  st.selSlot = null;
  st.selJogador = null;
}

function jorn(c: Clube, id: number | null): Jogador | undefined {
  return jogadorPorId(c, id);
}

export function escMudar(campo: string, valor: string): void {
  const st = app.escalar;
  if (!st) return;
  const c = humano();
  if (campo === 'esc-formacao') {
    if (valor === 'reset') {
      st.titulares = st.titulares.map(() => null);
      st.reservas = [];
    } else if (valor === 'auto') {
      st.formacao = 'auto';
      const r = resolverEscalacao({ ...c, tatica: { ...c.tatica, formacao: 'auto', titulares: null, reservas: null, estilo: st.estilo } });
      st.titulares = r.escalacao.titulares.map((j) => j.id);
      st.reservas = r.escalacao.reservas.map((j) => j.id);
    } else if (valor) {
      const f = valor as Formacao;
      const esc2 = escalarAutoFormacao(c, f, st.estilo);
      st.formacao = f;
      st.titulares = esc2.titulares.map((j) => j.id);
      st.reservas = esc2.reservas.map((j) => j.id);
    }
    st.selSlot = null;
    st.selJogador = null;
  } else if (campo === 'esc-estilo') st.estilo = valor as EstadoEscalar['estilo'];
  else if (campo === 'esc-passe') st.passe = valor as Passe;
  else if (campo === 'esc-pressao') st.pressao = valor as Pressao;
  else if (campo.startsWith('esc-papel-')) definirPapel(st, Number(campo.slice(10)), valor as Papel | '');
  else if (campo === 'esc-marcacao') st.marcacao = valor as EstadoEscalar['marcacao'];
  else if (campo === 'esc-ataque') st.ataque = valor as EstadoEscalar['ataque'];
  else if (campo === 'esc-manter') st.manter = valor === 'true';
  else if (campo.startsWith('esc-pl-')) {
    const [, , tipo, n] = campo.split('-');
    const item = st.plano[Number(n)];
    if (!item) return;
    if (tipo === 'sai') item.saiId = Number(valor) || 0;
    else if (tipo === 'entra') item.entraId = Number(valor) || 0;
    else if (tipo === 'min') item.minuto = Number(valor);
    else if (tipo === 'cond') item.condicao = valor as CondicaoJogo;
  } else if (campo.startsWith('esc-aj-')) {
    const [, , tipo, n] = campo.split('-');
    const item = st.ajustes[Number(n)];
    if (!item) return;
    if (tipo === 'cond') item.condicao = valor as CondicaoJogo;
    else if (tipo === 'estilo') item.estilo = (valor || undefined) as Estilo | undefined;
    else if (tipo === 'marc') item.marcacao = (valor || undefined) as Marcacao | undefined;
    else if (tipo === 'passe') item.passe = (valor || undefined) as Passe | undefined;
    else if (tipo === 'press') item.pressao = (valor || undefined) as Pressao | undefined;
    else if (tipo === 'ata') item.ataque = (valor || undefined) as ConcentracaoAtaque | undefined;
  } else if (campo.startsWith('esc-fn-')) {
    const qual = campo.slice(7);
    const id = valor && valor !== 'true' && valor !== 'false' ? Number(valor) : null;
    if (qual === 'capitao') st.funcoes.capitaoId = id;
    else if (qual === 'penalti') st.funcoes.batedorPenaltiId = id;
    else if (qual === 'falta') st.funcoes.batedorFaltaId = id;
    else if (qual === 'escanteio') st.funcoes.batedorEscanteioId = id;
  }
}

/** Valida e grava a tática do dialog no clube. Devolve false (com mensagem) se a escalação estiver incompleta. */
export function aplicarEscalar(): boolean {
  const st = app.escalar;
  if (!st) return true;
  const c = humano();
  const slots = slotsDaFormacao(formacaoEfetiva(st));
  const ids = st.titulares;
  if (ids.length !== slots.length || ids.some((x) => x === null)) {
    app.mensagem = 'Selecione 11 jogadores.';
    return false;
  }
  const lista = ids as number[];
  if (new Set(lista).size !== lista.length) {
    app.mensagem = 'Há jogador repetido na escalação.';
    return false;
  }
  const bancoValido = st.reservas.filter((id) => !lista.includes(id));

  // plano de jogo: descarta linhas incompletas e confere quem sai (titular) e quem entra (reserva)
  const completas = st.plano.filter((s) => s.saiId && s.entraId);
  for (const s of completas) {
    const sai = jogadorPorId(c, s.saiId);
    const entra = jogadorPorId(c, s.entraId);
    if (!lista.includes(s.saiId) || !bancoValido.includes(s.entraId) || !sai || !entra) {
      app.mensagem = 'Plano de jogo: quem sai deve ser titular e quem entra, reserva do banco. Ajuste as substituições planejadas.';
      return false;
    }
    if ((sai.posicao === 'G') !== (entra.posicao === 'G')) {
      app.mensagem = `Plano de jogo: ${sai.nome} e ${entra.nome} — goleiro só é trocado por outro goleiro.`;
      return false;
    }
  }
  st.funcoes.papeis = papeisValidos(c, st);
  let plano: PlanoDeJogo;
  try {
    plano = normalizarPlano(c, { substituicoes: completas, ajustes: st.ajustes, ...st.funcoes });
  } catch (erro) {
    app.mensagem = `Plano de jogo: ${erro instanceof Error ? erro.message : 'inválido'}.`;
    return false;
  }
  st.plano = plano.substituicoes ?? [];
  st.ajustes = plano.ajustes ?? [];

  if (st.formacao === 'auto') {
    c.tatica = { ...c.tatica, ...plano, formacao: 'auto', titulares: null, reservas: bancoValido, estilo: st.estilo, marcacao: st.marcacao, ataque: st.ataque, passe: st.passe, pressao: st.pressao };
  } else {
    c.tatica = { ...c.tatica, ...plano, formacao: st.formacao, titulares: lista, reservas: bancoValido, estilo: st.estilo, marcacao: st.marcacao, ataque: st.ataque, passe: st.passe, pressao: st.pressao };
  }
  return true;
}

/** Depois de jogar, "escalação padrão" desmarcada volta à escalação automática. */
export function aposJogar(): void {
  const st = app.escalar;
  if (st && !st.manter) {
    const c = humano();
    c.tatica = { ...c.tatica, formacao: 'auto', titulares: null, reservas: null };
  }
  app.escalar = null;
}

export function notaPosicao(j: Jogador, slot: Posicao): number {
  return Math.round(forcaBase(j) * fatorPosicao(j.posicao, slot) * 10);
}

// ---- Escalações salvas (dois slots por clube, guardados neste navegador) ---------------------------

const SLOTS_SALVOS = 2;

interface EscalacaoSalva {
  formacao: Formacao;
  titulares: (number | null)[];
  reservas: number[];
  estilo: Estilo;
  marcacao: Marcacao;
  ataque: ConcentracaoAtaque;
  passe: Passe;
  pressao: Pressao;
  funcoes: EstadoEscalar['funcoes'];
  plano: EstadoEscalar['plano'];
  ajustes: EstadoEscalar['ajustes'];
  salvaEm: number;
}

const chaveSalvas = (c: Clube): string => `webfoot-escalacoes:${c.nome}`;

function lerSalvas(c: Clube): (EscalacaoSalva | null)[] {
  try {
    const bruto = JSON.parse(localStorage.getItem(chaveSalvas(c)) ?? '[]') as unknown;
    const lista = Array.isArray(bruto) ? bruto : [];
    return Array.from({ length: SLOTS_SALVOS }, (_, i) => {
      const e = lista[i] as EscalacaoSalva | null | undefined;
      return e && typeof e === 'object' && Array.isArray(e.titulares) && (LISTA_FORMACOES as string[]).includes(e.formacao) ? e : null;
    });
  } catch {
    return Array.from({ length: SLOTS_SALVOS }, () => null);
  }
}

function gravarSalvas(c: Clube, lista: (EscalacaoSalva | null)[]): boolean {
  try {
    localStorage.setItem(chaveSalvas(c), JSON.stringify(lista));
    return true;
  } catch {
    return false;
  }
}

function resumoSalva(s: EscalacaoSalva): string {
  return `${s.formacao} · ${ROTULO_ESTILO[s.estilo] ?? s.estilo} · ${new Date(s.salvaEm).toLocaleDateString('pt-BR')}`;
}

function painelSalvas(c: Clube): string {
  const salvas = lerSalvas(c);
  const cartoes = salvas
    .map(
      (s, i) => `<div class="esc-salva"><b>Slot ${i + 1}</b><span class="${s ? '' : 'cinza'} peq">${s ? esc(resumoSalva(s)) : 'vazio'}</span>
        <button type="button" class="btn peq" data-a="esc-salvar" data-i="${i}">Salvar escalação</button>
        <button type="button" class="btn peq" data-a="esc-carregar" data-i="${i}" ${s ? '' : 'disabled'}>Carregar</button></div>`,
    )
    .join('');
  return `<div class="esc-salvas" title="Guarda formação, titulares, banco, mentalidade, passe, pressão, funções e plano de jogo">${cartoes}</div>`;
}

/** Guarda a escalação da tela no slot. A formação "automática" é gravada como a formação que ela resolveu naquele momento. */
function salvarEscalacao(st: EstadoEscalar, i: number): void {
  const c = humano();
  const lista = lerSalvas(c);
  lista[i] = {
    formacao: formacaoEfetiva(st),
    titulares: [...st.titulares],
    reservas: [...st.reservas],
    estilo: st.estilo,
    marcacao: st.marcacao,
    ataque: st.ataque,
    passe: st.passe,
    pressao: st.pressao,
    funcoes: { ...st.funcoes, papeis: { ...(st.funcoes.papeis ?? {}) } },
    plano: st.plano.map((s) => ({ ...s })),
    ajustes: st.ajustes.map((a) => ({ ...a })),
    salvaEm: Date.now(),
  };
  app.mensagem = gravarSalvas(c, lista) ? `Escalação guardada no slot ${i + 1}.` : 'Não foi possível guardar (armazenamento do navegador cheio ou bloqueado).';
}

/** Traz a escalação do slot para a tela. Quem saiu do elenco, está lesionado ou suspenso deixa a vaga vazia. */
function carregarEscalacao(st: EstadoEscalar, i: number): void {
  const c = humano();
  const s = lerSalvas(c)[i];
  if (!s) return;
  const ok = (id: number | null | undefined): id is number => id !== null && id !== undefined && c.elenco.some((j) => j.id === id && disponivel(j));
  const slots = slotsDaFormacao(s.formacao);
  const usados = new Set<number>();
  const novos = slots.map((_, k) => {
    const id = s.titulares[k];
    if (!ok(id) || usados.has(id)) return null;
    usados.add(id);
    return id;
  });
  const faltam = novos.filter((x) => x === null).length;
  st.formacao = s.formacao;
  st.titulares = novos;
  st.reservas = s.reservas.filter((id) => ok(id) && !usados.has(id)).slice(0, BANCO_MAX);
  st.estilo = s.estilo;
  st.marcacao = s.marcacao;
  st.ataque = s.ataque;
  st.passe = s.passe;
  st.pressao = s.pressao;
  const existe = (id: number | null | undefined): number | null => (id !== null && id !== undefined && c.elenco.some((j) => j.id === id) ? id : null);
  st.funcoes = {
    ...st.funcoes,
    capitaoId: existe(s.funcoes?.capitaoId),
    batedorPenaltiId: existe(s.funcoes?.batedorPenaltiId),
    batedorFaltaId: existe(s.funcoes?.batedorFaltaId),
    batedorEscanteioId: existe(s.funcoes?.batedorEscanteioId),
    papeis: s.funcoes?.papeis ?? {},
  };
  st.funcoes.papeis = papeisValidos(c, st);
  const reservasOk = new Set(st.reservas);
  st.plano = (s.plano ?? []).filter((p) => usados.has(p.saiId) && reservasOk.has(p.entraId)).map((p) => ({ ...p }));
  st.ajustes = (s.ajustes ?? []).map((a) => ({ ...a }));
  st.selSlot = null;
  st.selJogador = null;
  app.mensagem = faltam > 0 ? `Slot ${i + 1} carregado: ${faltam} ${faltam === 1 ? 'vaga ficou vazia' : 'vagas ficaram vazias'} (jogador indisponível ou fora do elenco).` : `Escalação do slot ${i + 1} carregada.`;
}

// ---- Funções individuais e leitura do adversário --------------------------------------------------

function papeisValidos(c: Clube, st: EstadoEscalar): Record<number, Papel> {
  const slots = slotsDaFormacao(formacaoEfetiva(st));
  const out: Record<number, Papel> = {};
  st.titulares.forEach((id, i) => {
    if (id === null) return;
    const p = st.funcoes.papeis?.[id];
    if (p && PAPEIS[p].posicao === slots[i] && jogadorPorId(c, id)) out[id] = p;
  });
  return out;
}

function definirPapel(st: EstadoEscalar, id: number, papel: Papel | ''): void {
  const mapa: Record<number, Papel> = { ...(st.funcoes.papeis ?? {}) };
  if (!papel) delete mapa[id];
  else {
    if (!mapa[id] && Object.keys(mapa).length >= MAX_PAPEIS) {
      app.mensagem = `No máximo ${MAX_PAPEIS} funções individuais ao mesmo tempo.`;
      return;
    }
    mapa[id] = papel;
  }
  st.funcoes.papeis = mapa;
}

function painelPapeis(c: Clube, st: EstadoEscalar): string {
  const slots = slotsDaFormacao(formacaoEfetiva(st));
  const ativos = papeisValidos(c, st);
  const linhas = slots
    .map((slot, i) => {
      const j = jogadorPorId(c, st.titulares[i] ?? null);
      const lista = papeisDaPosicao(slot);
      if (!j || lista.length === 0) return '';
      const atual = ativos[j.id] ?? null;
      const apt = atual ? aptidaoPapel(atual, j) : 0;
      const encaixe = apt >= 1.1 ? 'ótimo encaixe' : apt >= 0.9 ? 'bom encaixe' : 'encaixe fraco';
      return `<div class="pl-linha pl-papel"><span class="papel-jog">${esc(j.nome)} <span class="cinza">(${NOME_POSICAO[slot]})</span></span>
        <select data-ch="esc-papel-${j.id}">${opcoes<Papel>(atual, lista.map((x): [Papel, string] => [x, PAPEIS[x].nome]), 'sem função')}</select>
        ${atual ? `<span class="cinza peq">${esc(encaixe)} · ${esc(PAPEIS[atual].descricao)}</span>` : ''}</div>`;
    })
    .join('');
  return `<div class="pn-tit"><span>Funções dos jogadores (${Object.keys(ativos).length}/${MAX_PAPEIS})</span></div>
    <p class="cinza peq">Muda o que o jogador faz em campo, não só a força. O efeito depende do encaixe (características e habilidades dele) e vale enquanto ele está em campo na posição da função.</p>
    ${linhas || '<p class="cinza">Escale os 11 titulares para escolher as funções.</p>'}`;
}

/** Leitura tática do confronto: o jeito habitual do adversário contra o que está montado na tela. */
function leituraDoAdversario(c: Clube, adv: Clube, st: EstadoEscalar): string {
  const slots = slotsDaFormacao(formacaoEfetiva(st));
  const titulares = st.titulares.map((id) => jogadorPorId(c, id)).filter((j): j is Jogador => Boolean(j));
  if (titulares.length !== slots.length) return '';
  const cond = condicaoDaEscalacao({ titulares, reservas: [], slots });
  const linhas = leituraTatica({ estilo: st.estilo, passe: st.passe, pressao: st.pressao, marcacao: st.marcacao, ataque: st.ataque }, cond, adv, resolverEscalacao(adv).escalacao);
  return `<div class="leitura-tatica">${linhas.map((l) => `<p class="cinza peq">${esc(l)}</p>`).join('')}</div>`;
}